<template>
  <div class="dates-list">
    <v-expansion-panels
      v-model="futureOccurrencesPanels"
      :readonly="futureOccurrences.length === 1"
    >
      <v-row no-gutters class="row-10">
        <v-col
          v-for="(occurrence, index) of parsedDates(futureOccurrences)"
          :key="'event-date-' + index"
          cols="10"
        >
          <v-expansion-panel class="elevation-0 mb-2">
            <v-expansion-panel-header class="date-card pa-0">
              <div class="date-col">
                <h3 class="text-uppercase font-weight-bold mb-0">
                  {{ $d(occurrence.momentStartAt, 'ddd') }}
                </h3>
                <h1 class="mb-0">{{ $d(occurrence.momentStartAt, 'DD') }}</h1>
                <h3 class="text-uppercase font-weight-bold mb-2">
                  {{ $d(occurrence.momentStartAt, 'MMM_YY') }}
                </h3>
                <h3 class="font-weight-regular mb-0">
                  {{ $d(occurrence.momentStartAt, 'HH_mm') }}
                </h3>
              </div>
              <div class="info-col">
                <p class="event-name font-weight-bold mb-4">
                  {{ eventName(occurrence) }}
                </p>
                <div>
                  <p class="one-line">
                    {{ occurrence.venue && occurrence.venue.name }}
                  </p>
                  <p class="mb-0 one-line">
                    {{ formattedCity(occurrence) }}
                  </p>
                  <p
                    v-if="isMobile && hasTicketConfig(occurrence)"
                    class="mt-2 one-line"
                  >
                    {{ $t('main.from') }} {{ occurrence.smallerPrice }}
                  </p>
                </div>
              </div>
              <template #actions>
                <div v-if="!isMobile && hasTicketConfig(occurrence)">
                  <p class="font-weight-bold text-right mb-0">
                    {{ $t('main.from') }} {{ occurrence.smallerPrice }}
                  </p>
                </div>
                <div>
                  <div
                    v-if="
                      parsedDates(futureOccurrences).length > 1 &&
                      occurrence.timeslot?.length > 0
                    "
                  >
                    <PButton class="p-button-rounded" icon="pi pi-angle-down" />
                  </div>
                  <div
                    v-else-if="
                      !occurrence.timeslot || occurrence.timeslot.length === 0
                    "
                    class="py-0"
                  >
                    <PButton
                      :label="!isMobile ? $t('main.tickets') : ''"
                      :class="{ 'p-button-rounded': isMobile }"
                      icon="pi pi-angle-right"
                      icon-pos="right"
                      @click="
                        $emit('selectOccurrence', {
                          occurrence: occurrence
                        })
                      "
                    />
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content
              v-if="occurrence.timeslot && occurrence.timeslot.length > 0"
              class="pa-4 date-card-expanded"
            >
              <p class="one-line">
                {{ $t('cart.chooseTimeslot') }}
              </p>
              <div
                v-for="(timeslot, timeslotIndex) of occurrence.timeslot"
                :key="index + '_' + timeslotIndex"
                class="timeslot-row mt-4 pt-4"
              >
                <div class="flex-grow-1">
                  <h3>{{ timeslot.name }}</h3>
                  <h3>{{ formatTime(timeslot.startAt) }}</h3>
                  <h3>{{ formatTime(timeslot.endAt) }}</h3>
                </div>
                <div class="py-0 button-container">
                  <PButton
                    :label="!isMobile ? $t('main.tickets') : ''"
                    :class="{ 'p-button-rounded': isMobile }"
                    icon="pi pi-angle-right"
                    icon-pos="right"
                    @click="
                      $emit('selectOccurrence', {
                        occurrence: occurrence,
                        timeslot
                      })
                    "
                  />
                  <h3 class="remaining-tickets">
                    {{ showRemainingTicketsInTimeslot(timeslot) }}
                  </h3>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-col>
      </v-row>
    </v-expansion-panels>
    <v-row v-if="pastOccurrences.length > 0" class="py-8" no-gutters>
      <h3 class="mb-0 font-weight-bold text-uppercase back-text">
        {{ $t('cart.pastOccurrences') }}
      </h3>
    </v-row>
    <v-expansion-panels
      v-if="pastOccurrences.length > 0"
      v-model="pastOccurrencesPanels"
      :readonly="pastOccurrences.length === 1"
    >
      <v-row no-gutters class="row-10">
        <v-col
          v-for="(occurrence, index) of parsedDates(pastOccurrences)"
          :key="'event-date-' + index"
          cols="10"
        >
          <v-expansion-panel class="elevation-0 mb-2">
            <v-expansion-panel-header class="date-card past-event pa-0">
              <div class="date-col">
                <h3 class="text-uppercase font-weight-bold mb-0">
                  {{ $d(occurrence.momentStartAt, 'ddd') }}
                </h3>
                <h1 class="mb-0">{{ $d(occurrence.momentStartAt, 'DD') }}</h1>
                <h3 class="text-uppercase font-weight-bold mb-2">
                  {{ $d(occurrence.momentStartAt, 'MMM_YY') }}
                </h3>
                <h3 class="font-weight-regular mb-0">
                  {{ $d(occurrence.momentStartAt, 'HH_mm') }}
                </h3>
              </div>
              <div class="info-col">
                <p class="event-name font-weight-bold mb-4">
                  {{ eventName(occurrence) }}
                </p>
                <div>
                  <p class="one-line">
                    {{ occurrence.venue && occurrence.venue.name }}
                  </p>
                  <p class="mb-0 one-line">
                    {{ formattedCity(occurrence) }}
                  </p>
                  <p
                    v-if="isMobile && hasTicketConfig(occurrence)"
                    class="mt-2 one-line"
                  >
                    {{ $t('main.from') }} {{ occurrence.smallerPrice }}
                  </p>
                </div>
              </div>
              <template #actions>
                <div v-if="!isMobile && hasTicketConfig(occurrence)">
                  <p class="font-weight-bold text-right mb-0">
                    {{ $t('main.from') }} {{ occurrence.smallerPrice }}
                  </p>
                </div>
                <div>
                  <div
                    v-if="
                      parsedDates(pastOccurrences).length > 1 &&
                      occurrence.timeslot.length > 0
                    "
                  >
                    <PButton class="p-button-rounded" icon="pi pi-angle-down" />
                  </div>
                  <div
                    v-else-if="
                      !occurrence.timeslot || occurrence.timeslot.length === 0
                    "
                    class="py-0"
                  >
                    <PButton
                      :label="!isMobile ? $t('main.tickets') : ''"
                      :class="{ 'p-button-rounded': isMobile }"
                      icon="pi pi-angle-right"
                      icon-pos="right"
                      disabled
                    />
                  </div>
                </div>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content
              v-if="occurrence.timeslot && occurrence.timeslot.length > 0"
              class="pa-4 date-card-expanded"
            >
              <p class="one-line">
                {{ $t('cart.chooseTimeslot') }}
              </p>
              <div
                v-for="(timeslot, timeslotIndex) of occurrence.timeslot"
                :key="index + '_' + timeslotIndex"
                class="timeslot-row mt-4 pt-4"
              >
                <div class="flex-grow-1">
                  <h3>{{ timeslot.name }}</h3>
                  <h3>{{ formatTime(timeslot.startAt) }}</h3>
                  <h3>{{ formatTime(timeslot.endAt) }}</h3>
                </div>
                <div class="py-0">
                  <PButton
                    :label="!isMobile ? $t('main.tickets') : ''"
                    :class="{ 'p-button-rounded': isMobile }"
                    icon="pi pi-angle-right"
                    icon-pos="right"
                    disabled
                  />
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-col>
      </v-row>
    </v-expansion-panels>
  </div>
</template>

<script>
import moment from 'moment';
import PButton from 'primevue/button';
import { convertDateToDisplay } from '~/utils/date-time';
import {
  EVENT_STATES,
  BENCHMARK_FOR_AVAILABLE_TICKETS,
  sortByDisplayOrder
} from '~/utils/utils';

export default {
  name: 'DateList',
  components: {
    PButton
  },
  props: {
    occurrences: {
      type: Array,
      default() {
        return [];
      }
    },
    event: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      eventStates: EVENT_STATES
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    hasTicketConfig() {
      return (item) => item.ticketConfig.length > 0;
    },
    futureOccurrences() {
      return this.occurrences.filter((occurrence) => {
        return moment(occurrence.endAt).isAfter(moment());
      });
    },
    futureOccurrencesPanels: {
      get() {
        return this.futureOccurrences.length === 1 ? 0 : null;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    pastOccurrencesPanels: {
      get() {
        return this.pastOccurrences.length === 1 ? 0 : null;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    pastOccurrences() {
      return this.occurrences.filter((occurrence) => {
        return moment(occurrence.endAt).isBefore(moment());
      });
    }
  },
  methods: {
    // prints message about number of ticket that can be bought in the timeslot if that number is lower than defined benchmark
    showRemainingTicketsInTimeslot(timeslot) {
      // available tickets based on ticket config information
      let availableTicketConfigTickets = 0;
      // calculates available tickets for limited timeslot if some tickets were already bought
      if (
        timeslot.ticketConfigCapacity > 0 &&
        timeslot?.associatedTicketConfig.length > 0
      ) {
        timeslot.associatedTicketConfig.forEach((atc) => {
          const foundTicketConfig = timeslot.ticketConfig.find(
            (tc) => tc.id === atc
          );
          availableTicketConfigTickets += foundTicketConfig.availableTickets;
        });
      }
      // calculates available tickets for regular timeslot or limited on if no tickets were bought
      else {
        timeslot.ticketConfig.forEach((tc) => {
          availableTicketConfigTickets += tc.availableTickets;
        });
      }
      // available tickets are calculated based on timeslot capacity and previously calculated number
      const remainingTickets = Math.min(
        timeslot.availableTickets,
        availableTicketConfigTickets
      );
      if (remainingTickets < BENCHMARK_FOR_AVAILABLE_TICKETS) {
        return this.$t('cart.remainingTicketsInTimeslot', {
          remainingTickets,
          timeslotCapacity: timeslot.capacity
        }).toUpperCase();
      }
    },
    formattedCity(occurrence) {
      const data = [];
      if (occurrence.venue?.address?.city?.length > 0) {
        data.push(occurrence.venue?.address?.city);
      }
      if (occurrence.venue?.address?.country?.length > 0) {
        data.push(occurrence.venue?.address?.country);
      }
      return data.join(', ');
    },
    parsedDates(occurrences) {
      return occurrences.map((occurrence) => ({
        ...occurrence,
        timeslot: sortByDisplayOrder(occurrence?.timeslot),
        momentStartAt: moment(occurrence.startAt)
      }));
    },
    eventName(occurrence) {
      // Fallback if the occurrence does not have a name, which shouldn't happen
      const name = occurrence?.name ?? this.event.name;
      const status = occurrence?.status ?? this.event.status;
      if (status === this.eventStates.CANCELED) {
        return `${name} [${this.$t(
          'myEvents.statusFilter.canceled'
        ).toUpperCase()}] `;
      }
      return name;
    },
    formatTime(time) {
      const localTime = convertDateToDisplay(time, this.event.timezoneName);
      return this.$d(localTime, 'HH_mm');
    }
  }
};
</script>

<style lang="scss">
.dates-list {
  .v-expansion-panels {
    z-index: 0;
    display: block;
  }
  .theme--dark.v-expansion-panels .v-expansion-panel {
    background-color: var(--v-primary-lighten1);
    border-width: 1px;
    border-radius: 8px;
    border-style: solid;
    border-color: var(--v-primary-lighten3);
  }
  .theme--light.v-expansion-panels .v-expansion-panel {
    background-color: var(--v-backgroundGray-lighten1);
    border-width: 1px;
    border-radius: 8px;
    border-style: solid;
    border-color: var(--v-borderPrimary-lighten4);
  }
  .v-expansion-panel:before {
    box-shadow:
      0 0 0 0 rgba(0, 0, 0, 0.2),
      0 0 0 0 rgba(0, 0, 0, 0.14),
      0 0 0 0 rgba(0, 0, 0, 0.12) !important;
  }
  .v-expansion-panel-header {
    padding: 1rem;
    @media (max-width: 600px) {
      padding: 8px;
    }
    align-items: stretch;
  }
  .v-expansion-panel-content {
    padding: 1rem;
    @media (max-width: 600px) {
      padding: 8px;
    }
    border-top: 1px solid var(--v-borderPrimary-lighten4);
    background-color: var(--v-backgroundGray-lighten1);
    border-bottom-right-radius: 1px;
    border-bottom-left-radius: 1px;
  }
}
.v-application {
  .date-card {
    border-radius: 0;
    overflow: hidden;
    .date-col {
      flex: 0;
      padding-top: 1rem;
      padding-bottom: 1rem;
      min-width: 6.5rem;
      align-items: center;
      justify-content: center;
      display: flex;
      flex-direction: column;
      @media (max-width: 600px) {
        padding-top: 6px;
        padding-bottom: 6px;
        min-width: 71px;
      }
      h3 {
        text-align: center;
        color: var(--v-textPrimary-base);
        white-space: nowrap;
        @media (max-width: 600px) {
          font-size: 10px;
          line-height: 12px;
        }
      }
      h1 {
        font-size: 3.0625rem;
        line-height: 3.25rem;
        text-align: center;
        color: var(--v-textPrimary-base);
        white-space: nowrap;
        @media (max-width: 600px) {
          font-size: 22px;
          line-height: 26px;
        }
      }
    }
    .info-col {
      min-width: 1px;
      padding: 12px 16px;
      @media (max-width: 600px) {
        padding: 6px 8px;
      }
      border-left: 1px solid var(--v-borderPrimary-lighten4);
      p,
      a {
        color: var(--v-textPrimary-base);
        margin-bottom: 0;
      }
      .event-name {
        min-height: 3.15rem;
        @media (max-width: 600px) {
          font-size: 14px;
          line-height: 18px;
          min-height: 34px;
        }
      }
    }
    .v-expansion-panel-header__icon {
      display: flex;
      flex-direction: column;
      align-items: end;
      justify-content: space-between;
      margin: 12px 16px 12px 0;
      @media (max-width: 600px) {
        flex-direction: row;
        align-items: center;
        margin: 6px 8px 6px 0;
      }
    }
  }
  .past-event {
    cursor: default;
  }
}
.date-card-expanded {
  border-radius: 0;
  p {
    color: var(--v-backgroundPrimary-base);
  }
  .timeslot-row {
    h3 {
      color: var(--v-backgroundPrimary-base);
    }
    h3.remaining-tickets {
      color: var(--v-backgroundHighlightWarning-base);
      text-align: end;
      margin-bottom: 0;
    }
    display: flex;
    border-top: 1px solid var(--v-borderPrimary-lighten4);
    .button-container {
      display: flex;
      flex-direction: column;
      width: 40%;
      gap: 0.5rem;
      align-items: end;
    }
  }
}
</style>
